/* eslint-disable react-refresh/only-export-components */
// Hook + Provider + Helper in einer Datei — HMR-Suboptimum hier akzeptiert,
// spart zwei Dateien für eng verwandte Exports.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { track } from "../lib/analytics";
import { writeCookie, COOKIE_VARIANT } from "../lib/cookies";
import type { VariantId } from "../lib/tokens";

type Ctx = {
  variant: VariantId;
  setVariant: (v: VariantId) => void;
};

const VariantContext = createContext<Ctx | null>(null);

export function VariantProvider({
  children,
  initialVariant = "editorial",
}: {
  children: ReactNode;
  initialVariant?: VariantId;
}) {
  // SSR + Client starten mit demselben Wert (aus scrolly_variant-Cookie).
  // Kein useEffect-Catchup, kein Hydration-Mismatch.
  const [variant, setVariantState] = useState<VariantId>(initialVariant);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-variant", variant);
  }, [variant]);

  const setVariant = useCallback((v: VariantId) => {
    setVariantState((prev) => {
      if (prev === v) return prev;
      writeCookie(COOKIE_VARIANT, v, { days: 365 });
      track("variant_switched", { from: prev, to: v });
      return v;
    });
  }, []);

  return (
    <VariantContext.Provider value={{ variant, setVariant }}>
      {children}
    </VariantContext.Provider>
  );
}

export function useVariant(): Ctx {
  const ctx = useContext(VariantContext);
  if (!ctx) {
    // Fallback außerhalb des Providers — nicht-interaktiv.
    return {
      variant: "editorial",
      setVariant: () => {
        /* no-op */
      },
    };
  }
  return ctx;
}
